/**
 * @file AttachmentHeldPushAlerts.tsx
 * @description The held-push alerts at the head of the attachments panel: the
 * saves this reader's desktop agent tried to send and the server refused.
 * Consumer: `Attachments`, which passes the path and the attachment map it
 * already holds.
 *
 * It opens its own listener through `useHeldAttachmentPushes` rather than taking
 * a prop, the way `DesktopKeepBanner` does, so no host has to know the feature
 * exists. Every sentence, every severity and the grouping by reason belong to
 * `resolveHeldAttachmentPushAlerts`; this file owns the listener, the dismissal
 * and the export gate.
 *
 * `usePdfExportContext` is read here and fed into the hook, so an export tree
 * neither renders an alert nor opens a listener. A control printed onto a
 * rendered document is a defect, not a state of the UI.
 *
 * A reader with no pin at all is gated the same way, and for a plainer reason: a
 * held push can only exist for a user whose agent pushes, which takes a pin. So
 * the listener would open on every artifact page for everyone who keeps nothing
 * and find nothing every time. `useDesktopPins` is already open and refcounted
 * on this page, so the test costs no read of its own.
 *
 * Dismissing deletes the record, which is the only write the rules permit. There
 * is no client-side suppression behind it on purpose: a fresh refusal writes the
 * record again, and a still-held push must not stay invisible because someone
 * once clicked away.
 *
 * The delete carries no `userMessage`, so `handleFirestoreWriteError` speaks its
 * own `permission-denied` sentence, but a reader looking at their own alert is
 * not refused: the delete lane wants the artifact's read lane plus
 * `resource.data.userId == request.auth.uid`, so whoever can see the record can
 * dismiss it, whatever their write rights and whatever state the form version is
 * in. The catch remains for the ordinary write failures, and for the narrow race
 * where the server clears the record between the render and the click.
 */

import { FC, useCallback, useContext, useState } from 'react';
import { Alert, Button, Typography } from 'antd';

import type { ArtifactAttachments } from '#types/System.types';
import type { HeldAttachmentPushReason } from '#types';
import FlexCol from '../../atoms/FlexCol';
import { MessageContext } from '../../../providers/MessageContext';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';
import { useDesktopPins } from '../../../use/desktopSync/useDesktopPins';
import { useHeldAttachmentPushes } from '../../../use/desktopSync/useHeldAttachmentPushes';
import {
    resolveHeldAttachmentPushAlerts,
    type HeldAttachmentPushAlert,
} from './resolveHeldAttachmentPushAlerts';

const { Text } = Typography;

interface AttachmentHeldPushAlertsProps {
    /** The artifact whose `heldPushes` subcollection this reads. */
    artifactPath: string | undefined;
    /** The artifact's attachment map, which supplies the file names. */
    attachments: ArtifactAttachments;
}

export const AttachmentHeldPushAlerts: FC<AttachmentHeldPushAlertsProps> = ({
    artifactPath,
    attachments,
}) => {
    const { isPdfExportMode } = usePdfExportContext();
    const { handleFirestoreWriteError } = useContext(MessageContext);
    const { pins, loading: pinsLoading } = useDesktopPins();
    const hasAnyPin = Object.keys(pins).length > 0;
    const { pushes, loading, dismiss } = useHeldAttachmentPushes(
        isPdfExportMode || !hasAnyPin ? null : artifactPath,
    );
    const [dismissing, setDismissing] = useState<HeldAttachmentPushReason | null>(null);

    const dismissAlert = useCallback(async (pushAlert: HeldAttachmentPushAlert) => {
        setDismissing(pushAlert.reason);
        try {
            await dismiss(pushAlert.fileUids);
        } catch (error) {
            handleFirestoreWriteError(error, '[AttachmentHeldPushAlerts] dismiss');
        } finally {
            setDismissing(null);
        }
    }, [dismiss, handleFirestoreWriteError]);

    if (isPdfExportMode || !hasAnyPin || pinsLoading || loading) {
        return null;
    }

    const alerts = resolveHeldAttachmentPushAlerts({
        pushes,
        attachments,
        nowMillis: Date.now(),
    });

    if (alerts.length === 0) {
        return null;
    }

    return (
        <FlexCol gap={5} id="attachment-held-pushes">
            {alerts.map((pushAlert) => (
                <Alert
                    key={pushAlert.reason}
                    id={`attachment-held-push-${pushAlert.reason}`}
                    aria-label={pushAlert.ariaLabel}
                    type={pushAlert.severity}
                    showIcon
                    title={<Text strong>{pushAlert.title}</Text>}
                    description={
                        <FlexCol gap={5}>
                            <Text>{pushAlert.body}</Text>
                            {pushAlert.age !== null && (
                                <Text type="secondary">{`Last tried ${pushAlert.age}.`}</Text>
                            )}
                            <Text type="secondary">{pushAlert.dismissNote}</Text>
                        </FlexCol>
                    }
                    action={
                        <Button
                            size="small"
                            loading={dismissing === pushAlert.reason}
                            onClick={() => void dismissAlert(pushAlert)}
                            id={`attachment-held-push-dismiss-${pushAlert.reason}`}
                            aria-label={pushAlert.dismissAriaLabel}
                        >
                            Dismiss
                        </Button>
                    }
                />
            ))}
        </FlexCol>
    );
};
